/**
 * CollapsibleSection Component
 * A reusable collapsible section for the plan editor with smooth transitions
 * and accessibility features
 */

import React, { useState } from 'react';

export interface CollapsibleSectionProps {
  title: string;
  icon?: React.ReactNode;
  badge?: string | number;
  defaultOpen?: boolean;
  hasChanges?: boolean;
  children: React.ReactNode;
}

export function CollapsibleSection({
  title,
  icon,
  badge,
  defaultOpen = false,
  hasChanges = false,
  children,
}: CollapsibleSectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Handle Enter and Space keys for accessibility
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleToggle();
    }
  };

  return (
    <div className="bg-white border border-neutral-200 rounded-lg overflow-hidden">
      {/* Header */}
      <button
        type="button"
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
        className="w-full px-6 py-4 flex items-center justify-between hover:bg-neutral-50 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-inset"
        aria-expanded={isOpen}
        aria-controls={`collapsible-content-${title.replace(/\s+/g, '-').toLowerCase()}`}
      >
        <div className="flex items-center gap-3 flex-1">
          {/* Optional Icon */}
          {icon && (
            <span className="flex-shrink-0 text-neutral-600" aria-hidden="true">
              {icon}
            </span>
          )}

          {/* Title */}
          <h3 className="text-base font-semibold text-neutral-900">{title}</h3>

          {/* Optional Badge */}
          {badge !== undefined && (
            <span
              className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-neutral-100 text-neutral-700"
              aria-label={`${badge} items`}
            >
              {badge}
            </span>
          )}

          {/* Change Indicator */}
          {hasChanges && (
            <span
              className="inline-flex items-center gap-1.5 text-xs font-medium text-orange-600"
              aria-label="Has unsaved changes"
            >
              <span className="w-2 h-2 bg-orange-500 rounded-full" aria-hidden="true"></span>
              <span className="hidden sm:inline">Unsaved</span>
            </span>
          )}
        </div>

        {/* Chevron Icon */}
        <svg
          className={`w-5 h-5 text-neutral-500 transition-transform duration-200 flex-shrink-0 ml-2 ${
            isOpen ? 'rotate-180' : 'rotate-0'
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {/* Content */}
      <div
        id={`collapsible-content-${title.replace(/\s+/g, '-').toLowerCase()}`}
        className={`transition-all duration-200 ease-in-out ${
          isOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0 overflow-hidden'
        }`}
        role="region"
        aria-labelledby={`collapsible-header-${title.replace(/\s+/g, '-').toLowerCase()}`}
      >
        <div className="px-6 py-4 border-t border-neutral-200">{children}</div>
      </div>
    </div>
  );
}
